<template>
	<div class="upload-box" ref="uploadEl">
		<el-upload
			action="#"
			:id="id"
			:class="['upload']"
			:multiple="false"
			v-bind="$attrs"
			:disabled="disabled"
			:show-file-list="false"
			:http-request="handleHttpUpload"
			:before-upload="beforeUpload"
			:on-success="uploadSuccess"
			:on-error="uploadError"
			:style="uploadStyle"
			:drag="drag"
			:accept="accept"
			:limit="Infinity"
			v-loading="loading"
		>
			<template v-if="urlList.length">
				<video :src="urlList[0]" v-if="isVideo(urlList[0])"></video>
				<img :src="urlList[0]" v-else class="upload-image" />
			</template>
			<el-icon v-else class="upload-icon"><Plus /></el-icon>
			<div v-if="urlList.length" class="upload-handle" @click.stop>
				<div class="handle-icon" @click="editImg" v-if="!disabled">
					<el-icon class="pointer"><IEpEdit /></el-icon>
				</div>
				<div class="handle-icon" @click="handleView" v-if="canView">
					<el-icon class="pointer">
						<IEpVideoPlay v-if="isVideo(urlList[0])" />
						<IEpZoomIn v-else />
					</el-icon>
				</div>
				<div class="handle-icon" @click="downloadImg" v-if="(!disabled && !isSingle) || canDownload">
					<el-icon class="pointer"><IEpDownload /></el-icon>
				</div>
				<div class="handle-icon" @click="deleteImg" v-if="!disabled && canDelete">
					<el-icon class="pointer"><IEpDelete /></el-icon>
				</div>
			</div>
		</el-upload>
		<div class="el-upload__tip">
			<slot name="tip"></slot>
		</div>
		<el-image-viewer v-if="dialogVisible" @close="imageView" :url-list="urlList" />
		<!-- video播放器 -->
		<el-dialog title="Video Player" v-model="videoVisible" width="90%" destroy-on-close>
			<vue3videoPlay :src="urlList[0]" width="100%"></vue3videoPlay>
		</el-dialog>
	</div>
</template>

<script setup lang="ts">
import { Plus } from '@element-plus/icons-vue'
import { upload, getImageUrl, download, downloadOss } from '@/api/file'
import type { UploadProps, UploadRequestOptions } from 'element-plus'
import { downloadFile } from '@/utils/download'
import { isVideo } from '@shared/is'
import vue3videoPlay from 'vue3-video-play' // 引入组件
import 'vue3-video-play/dist/style.css' // 引入css
export type UploadFileProps = {
	mode?: 'single' | 'multiple' // 上传模式
	file?: string //文件
	fileList?: string[] // 文件地址 ==> 必传
	id?: string // 组件id ==> 非必传，当页面存在多个上传组件时必传（默认为upload）
	drag?: boolean // 是否支持拖拽上传 ==> 非必传（默认为true）
	disabled?: boolean // 是否禁用上传组件 ==> 非必传（默认为false）
	fileSize?: number // 单个文件大小限制 ==> 非必传（默认为 5M）
	uploadStyle?: { [key: string]: any } // 上传组件样式 ==> 非必传
	accept?: string // 上传文件类型 ==> 非必传（默认为 image/*）
	canDownload?: boolean // 是否可以下载 ==> 非必传（默认为 false）
	canView?: boolean // 是否可以查看 ==> 非必传（默认为 true）
	canDelete?: boolean // 是否可以删除 ==> 非必传（默认为 true）
	onSuccess?: (url: string, fileInfo?: any) => void // 上传成功后的回调函数 ==> 非必传
	afterDelete?: () => void; // 删除后的回调函数 ==> 非必传
}
const isSingle = computed(() => props.mode === 'single')
// 接受父组件参数
const props = withDefaults(defineProps<UploadFileProps>(), {
	id: 'upload',
	drag: false,
	disabled: false,
	fileSize: 5,
	accept: 'image/*',
	canView: true,
	canDelete: true,
	uploadStyle: () => ({ width: '175px', height: '175px' }),
	onSuccess: () => {}, // 默认空函数
	afterDelete: () => {}, // 默认空函数
})
interface UploadEmits {
	(e: 'update:file', value: string): void
	(e: 'update:fileList', value: string[]): void
	(e: 'check-validate'): void
}
const emit = defineEmits<UploadEmits>()
/**
 * @description 文件上传
 * @param options 上传的文件
 * */

const files = useVModel(props, 'fileList', emit)
const singleFile = useVModel(props, 'file', emit)
const loading = ref(false)
const handleHttpUpload = async (options: UploadRequestOptions) => {
	const formData = new FormData()
	formData.append('file', options.file)
	try {
		loading.value = true
		const file = await upload(formData)
		if (isSingle.value) {
			singleFile.value = file.ossFileUrl
			// 调用父组件的回调函数，传递完整的文件信息
			props.onSuccess(file.ossFileUrl, file)
		} else {
			files.value = [file.fileKey]
		}
		emit('check-validate')
	} catch (error) {
		options.onError(error as any)
	} finally {
		loading.value = false
	}
}

const urlList = computed(() => {
	const { file, fileList } = props
	return isSingle.value ? (file ? [file] : []) : (fileList ?? []).map(fileKey => getImageUrl(fileKey))
})

/**
 * @description 删除文件
 * */
const deleteImg = () => {
	files.value = []
	singleFile.value = ''
	props.afterDelete()
}

/**
 * @description 编辑文件
 * */
const uploadEl = ref<HTMLElement>()
const editImg = () => {
	if (!uploadEl.value) return
	const dom = uploadEl.value.querySelector(`.el-upload__input`)
	// const dom = document.querySelector(`#${props.id} .el-upload__input`)
	dom!.dispatchEvent(new MouseEvent('click'))
}
/**
 * @description 下载文件
 * */
const downloadImg = async () => {
	try {
		const response = await fetch(singleFile.value! + '?t=' + new Date().getUTCMilliseconds())
		if (!response.ok) {
			throw new Error('下载失败')
		}

		const blob = await response.blob()
		const url = window.URL.createObjectURL(blob)
		const link = document.createElement('a')
		link.href = url
		link.download = singleFile.value!.substring(singleFile.value!.lastIndexOf('/') + 1)
		document.body.appendChild(link)
		link.click()
		window.URL.revokeObjectURL(url)
		document.body.removeChild(link)
	} catch (error) {
		console.error('下载失败:', error)
		alert('下载失败，请重试')
	}
	// window.open(singleFile.value!, '_blank')
	// downloadImgZP(singleFile.value!)
	// loading.value = true
	// downloadOss(singleFile.value!)
	// 	.then(downloadFile)
	// 	.finally(() => {
	// 		loading.value = false
	// 	})
	// if (isSingle.value) {
	// 	downloadImgZP(singleFile.value!)
	// } else {
	// 	download(files.value![0]).then(downloadFile)
	// }
}

const downloadImgZP = (imgPath: string) => {
	const image = new Image()
	const filename = imgPath.split('__').pop()
	const fileType = filename?.split('.').pop()
	// 解决跨域 Canvas 污染问题
	image.setAttribute('crossOrigin', 'anonymous')
	console.log(image)
	image.onload = function () {
		const canvas = document.createElement('canvas')
		canvas.width = image.width
		canvas.height = image.height
		const context = canvas.getContext('2d')
		context!.drawImage(image, 0, 0, image.width, image.height)
		const url = canvas.toDataURL(`image/${fileType}`) // 得到文件的base64编码数据
		const a = document.createElement('a') // 生成一个a元素
		const event = new MouseEvent('click') // 创建一个单击事件
		a.download = filename! // 设置文件名称
		a.href = url // 将生成的URL设置为a.href属性
		a.dispatchEvent(event) // 触发a的单击事件
	}
	image.src = imgPath
}

/**
 * @description 文件上传之前判断
 * @param rawFile 上传的文件
 * */
const beforeUpload: UploadProps['beforeUpload'] = rawFile => {
	const imgSize = rawFile.size / 1024 / 1024 < props.fileSize
	if (!imgSize)
		ElNotification({
			title: '温馨提示',
			message: `上传文件大小不能超过 ${props.fileSize}MB!`,
			type: 'warning',
		})
	return imgSize
}

// 文件上传成功提示
const uploadSuccess = () => {
	ElNotification({
		title: '温馨提示',
		message: '文件上传成功！',
		type: 'success',
	})
}

// 文件上传错误提示
const uploadError = () => {
	ElNotification({
		title: '温馨提示',
		message: '文件上传失败，请您重新上传！',
		type: 'error',
	})
}

const handleView = () => {
	if (isVideo(urlList.value[0])) {
		videoPlay()
	} else {
		imageView()
	}
}

const dialogVisible = ref(false)
const imageView = () => {
	if (isVideo(urlList.value[0])) {
		//todo
	} else {
		dialogVisible.value = !dialogVisible.value
	}
}

/**
 * 视频播放
 */
const videoVisible = ref(false)
const videoPlay = () => {
	videoVisible.value = !videoVisible.value
}
</script>
<script lang="ts">
export default {
	name: 'UploadImg',
}
</script>
<style scoped lang="scss">
@import './index.scss';
</style>
